<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { RouterLink } from 'vue-router';
import type { FunctionalComponent } from 'vue';
import * as LucideIcons from 'lucide-vue-next';
import { useI18n } from 'vue-i18n';
import { useUiDisplayLocale } from '@/composables/useUiDisplayLocale';
import { useContentStore } from '@/stores/content';
import ApiResponsiveImage from '@/components/ApiResponsiveImage.vue';
import { type SiteLogo, isOptimisedSiteLogo } from '@/navTree';
import { onOfflineMediaImgError } from '@/utils/offlineMediaPlaceholder';
import {
  hasAnyBuildMetadata,
  viteBuildTime,
  viteGitSha,
  viteRelease,
} from '@/utils/buildMetadata';

const SearchIcon = (LucideIcons as Record<string, FunctionalComponent>).Search;
const GlobeIcon = (LucideIcons as Record<string, FunctionalComponent>).Globe;
const MapPinIcon = (LucideIcons as Record<string, FunctionalComponent>).MapPin;
const PhoneIcon = (LucideIcons as Record<string, FunctionalComponent>).Phone;
const MailIcon = (LucideIcons as Record<string, FunctionalComponent>).Mail;

const SOCIAL_ICON_MAP: Record<string, FunctionalComponent | undefined> = {
  Facebook: (LucideIcons as Record<string, FunctionalComponent>).Facebook,
  Youtube: (LucideIcons as Record<string, FunctionalComponent>).Youtube,
  Instagram: (LucideIcons as Record<string, FunctionalComponent>).Instagram,
  Linkedin: (LucideIcons as Record<string, FunctionalComponent>).Linkedin,
  Twitter: (LucideIcons as Record<string, FunctionalComponent>).Twitter,
};

const contentStore = useContentStore();
const { t } = useI18n();

const currentLocale = useUiDisplayLocale();
const resolvedNavigation = computed(() => contentStore.getResolvedNavigation(currentLocale.value));
const topNavData = computed(() => resolvedNavigation.value.topNavData);

const siteName = computed(() => topNavData.value?.siteName?.trim() || 'INDMO');
const siteSubtitle = computed(() => topNavData.value?.siteSubtitle?.trim() ?? '');
const siteRootUrl = computed(() => topNavData.value?.siteRootUrl ?? '/');

/** Footer column: prefer dedicated footer logo, else first header site logo. */
const footerBrandLogo = computed((): SiteLogo | null => {
  const footer = topNavData.value?.footerLogo;
  if (footer) {
    if (isOptimisedSiteLogo(footer)) return footer;
    if ('url' in footer && footer.url) return footer;
  }
  const site = topNavData.value?.siteLogos;
  const first = site?.[0];
  if (first) {
    if (isOptimisedSiteLogo(first)) return first;
    if ('url' in first && first.url) return first;
  }
  return null;
});

const footerLogoText = computed(() => topNavData.value?.footerLogoText?.trim() ?? '');

/** Heading next to the logo: CMS footer text, else site name. */
const footerBrandTitle = computed(() => footerLogoText.value || siteName.value);

/** Flattened top-menu tree for footer fallback when CMS footer columns are empty. */
const quickLinkItemsFromTopMenu = computed(() => {
  const out: { title: string; url: string }[] = [];
  for (const page of resolvedNavigation.value.topMenuLinks) {
    const pt = page.title?.trim();
    if (pt) out.push({ title: pt, url: page.url ?? '/' });
    for (const child of page.children) {
      const ct = child.title?.trim();
      if (ct) out.push({ title: ct, url: child.url ?? '/' });
    }
  }
  return out;
});

/**
 * CMS footer columns (title + links) from HomePage.footer_link_columns, resolved to locale;
 * if none configured, fall back to a single column derived from the top menu.
 */
const footerColumnsDisplay = computed(() => {
  const cms = resolvedNavigation.value.footerLinkColumns;
  if (cms?.length) return cms;
  const legacy = quickLinkItemsFromTopMenu.value;
  if (legacy.length) {
    return [{ title: t('footer.quickLinks'), items: legacy }];
  }
  return [];
});

const currentYear = new Date().getFullYear();

const footerContactAddress = computed(
  () => topNavData.value?.footerContactAddress?.trim() ?? ''
);
const footerContactPhone = computed(
  () => topNavData.value?.footerContactPhone?.trim() ?? ''
);
const footerContactEmail = computed(
  () => topNavData.value?.footerContactEmail?.trim() ?? ''
);

const showFooterContactColumn = computed(
  () =>
    Boolean(
      footerContactAddress.value || footerContactPhone.value || footerContactEmail.value
    )
);

const footerPhoneTelHref = computed(() => {
  const p = footerContactPhone.value;
  if (!p) return '';
  const compact = p.replace(/[^\d+]/g, '');
  return compact.length ? `tel:${compact}` : '';
});

const footerMailtoHref = computed(() => {
  const e = footerContactEmail.value;
  return e ? `mailto:${e}` : '';
});

const footerLegalLinks = computed(
  () => resolvedNavigation.value.footerLegalLinks ?? []
);

const socialLinks = computed(() => {
  const links = topNavData.value?.socialLinks ?? [];
  return links.map((link) => ({
    href: link.url,
    label: link.label || link.icon || link.url,
    icon: SOCIAL_ICON_MAP[link.icon ?? ''] ?? GlobeIcon ?? SearchIcon,
    modifier: (link.icon ?? '').trim(),
  }));
});

function socialLinkClass(modifier: string): string {
  const base = 'site-footer__social-link';
  const map: Record<string, string> = {
    Facebook: `${base} site-footer__social-link--facebook`,
    Youtube: `${base} site-footer__social-link--youtube`,
    Instagram: `${base} site-footer__social-link--instagram`,
    Linkedin: `${base} site-footer__social-link--linkedin`,
    Twitter: `${base} site-footer__social-link--twitter`,
  };
  return map[modifier] ?? base;
}

const footerCopy = computed(() => {
  const cmsCopy = topNavData.value?.copyrightText?.trim();
  if (cmsCopy) {
    const normalizedCopy = cmsCopy
      .replace(/^(?:Copyright\s+)?©\s*\d{4}\s*/i, '')
      .trim();
    return `Copyright © ${currentYear} ${normalizedCopy}`;
  }
  return `Copyright © ${currentYear} ${siteName.value}. Managed by the Government of Timor Leste. Designed and developed by Catalpa International`;
});

const footerBottomEl = ref<HTMLElement | null>(null);
const showBuildInfoTooltip = ref(false);

const hasBuildMetaHint = computed(() => hasAnyBuildMetadata());

const buildInfoLines = computed((): string[] => {
  const out: string[] = [];
  const rel = viteRelease();
  if (rel) {
    out.push(t('footer.buildRelease', { tag: rel }));
  }
  const bt = viteBuildTime();
  if (bt) {
    out.push(t('footer.buildTime', { time: bt }));
  }
  const sha = viteGitSha();
  if (sha) {
    out.push(t('footer.buildGit', { sha }));
  }
  if (!out.length) {
    out.push(t('footer.buildInfoUnavailable'));
  }
  return out;
});

function dismissBuildInfoTooltip() {
  showBuildInfoTooltip.value = false;
}

function onFooterStripPointerDown(ev: PointerEvent) {
  if ((ev.target as HTMLElement).closest('a')) {
    return;
  }
  showBuildInfoTooltip.value = !showBuildInfoTooltip.value;
}

let offClickListener: ((ev: Event) => void) | null = null;

function removeOffClickListener() {
  if (offClickListener) {
    document.removeEventListener('pointerdown', offClickListener, true);
    offClickListener = null;
  }
}

watch(showBuildInfoTooltip, (open) => {
  removeOffClickListener();
  if (!open) {
    return;
  }
  void nextTick(() => {
    offClickListener = (ev: Event) => {
      const el = footerBottomEl.value;
      const t = ev.target;
      if (t instanceof Node && el?.contains(t)) {
        return;
      }
      dismissBuildInfoTooltip();
    };
    setTimeout(() => {
      if (offClickListener) {
        document.addEventListener('pointerdown', offClickListener, true);
      }
    }, 0);
  });
});

onBeforeUnmount(() => {
  removeOffClickListener();
});
</script>

<template>
  <footer class="site-footer">
    <div class="site-footer__container">
      <div class="site-footer__grid">
        <div class="site-footer__column site-footer__column--brand">
          <RouterLink :to="siteRootUrl" class="site-footer__brand">
            <div
              v-if="footerBrandLogo"
              class="site-footer__brand-logo-wrap"
            >
              <ApiResponsiveImage
                v-if="isOptimisedSiteLogo(footerBrandLogo)"
                :image="footerBrandLogo"
                alt=""
                class="site-footer__brand-logo-img site-footer__brand-logo-img--optimised"
                sizes="64px"
                loading="lazy"
              />
              <img
                v-else
                :src="footerBrandLogo.url"
                alt=""
                class="site-footer__brand-logo-img"
                :width="footerBrandLogo.width"
                :height="footerBrandLogo.height"
                loading="lazy"
                @error="onOfflineMediaImgError"
              >
            </div>
            <p class="site-footer__brand-name">
              {{ footerBrandTitle }}
            </p>
          </RouterLink>
          <p
            v-if="siteSubtitle"
            class="site-footer__tagline"
          >
            {{ siteSubtitle }}
          </p>
          <div
            v-if="socialLinks.length && showFooterContactColumn"
            class="site-footer__social"
          >
            <div class="site-footer__social-links">
              <a
                v-for="(link, sIdx) in socialLinks"
                :key="`${sIdx}-${link.href}`"
                :href="link.href"
                target="_blank"
                rel="noopener noreferrer"
                :class="socialLinkClass(link.modifier)"
                :aria-label="link.label"
              >
                <component
                  :is="link.icon"
                  class="site-footer__social-icon"
                  aria-hidden="true"
                />
              </a>
            </div>
          </div>
        </div>

        <nav
          v-for="(column, cIdx) in footerColumnsDisplay"
          :key="`footer-nav-${cIdx}-${column.title}`"
          class="site-footer__column"
          :aria-label="column.title"
        >
          <p class="site-footer__heading">
            {{ column.title }}
          </p>
          <ul class="site-footer__link-list">
            <li
              v-for="(item, index) in column.items"
              :key="`fc-${cIdx}-${index}-${item.url}`"
            >
              <RouterLink
                :to="item.url"
                class="site-footer__link"
              >
                {{ item.title }}
              </RouterLink>
            </li>
          </ul>
        </nav>

        <div
          v-if="!showFooterContactColumn && socialLinks.length"
          class="site-footer__social-row"
        >
          <div class="site-footer__social-links">
            <a
              v-for="(link, sIdx) in socialLinks"
              :key="`social-col-${sIdx}-${link.href}`"
              :href="link.href"
              target="_blank"
              rel="noopener noreferrer"
              :class="socialLinkClass(link.modifier)"
              :aria-label="link.label"
            >
              <component
                :is="link.icon"
                class="site-footer__social-icon"
                aria-hidden="true"
              />
            </a>
          </div>
        </div>

        <div
          v-if="showFooterContactColumn"
          class="site-footer__column site-footer__column--contact"
        >
          <p class="site-footer__heading">
            {{ t('footer.contactUs') }}
          </p>
          <ul class="site-footer__contact-list">
            <li
              v-if="footerContactAddress"
              class="site-footer__contact-row"
            >
              <MapPinIcon
                class="site-footer__contact-icon"
                aria-hidden="true"
              />
              <span class="site-footer__contact-text">{{ footerContactAddress }}</span>
            </li>
            <li
              v-if="footerContactPhone"
              class="site-footer__contact-row"
            >
              <PhoneIcon
                class="site-footer__contact-icon"
                aria-hidden="true"
              />
              <a
                v-if="footerPhoneTelHref"
                :href="footerPhoneTelHref"
                class="site-footer__contact-text site-footer__contact-link"
              >{{ footerContactPhone }}</a>
              <span
                v-else
                class="site-footer__contact-text"
              >{{ footerContactPhone }}</span>
            </li>
            <li
              v-if="footerContactEmail"
              class="site-footer__contact-row"
            >
              <MailIcon
                class="site-footer__contact-icon"
                aria-hidden="true"
              />
              <a
                :href="footerMailtoHref"
                class="site-footer__contact-text site-footer__contact-link"
              >{{ footerContactEmail }}</a>
            </li>
          </ul>
        </div>
      </div>

      <div
        ref="footerBottomEl"
        class="site-footer__bottom"
        :class="{ 'site-footer__bottom--has-meta': hasBuildMetaHint }"
        @pointerdown="onFooterStripPointerDown"
      >
        <div
          v-show="showBuildInfoTooltip"
          class="site-footer__build-tooltip"
          role="tooltip"
          @pointerdown.stop
        >
          <p
            v-for="(line, idx) in buildInfoLines"
            :key="`build-line-${idx}`"
            class="site-footer__build-tooltip-line"
          >
            {{ line }}
          </p>
        </div>
        <p class="site-footer__copyright">
          {{ footerCopy }}
        </p>
        <nav
          v-if="footerLegalLinks.length"
          class="site-footer__legal"
          :aria-label="t('footer.legalNav')"
        >
          <RouterLink
            v-for="(item, legIdx) in footerLegalLinks"
            :key="`legal-${legIdx}-${item.url}`"
            :to="item.url"
            class="site-footer__legal-link"
            @pointerdown.stop
          >
            {{ item.title }}
          </RouterLink>
        </nav>
      </div>
    </div>
  </footer>
</template>

<style scoped>
.site-footer {
  flex-shrink: 0;
  width: 100%;
  margin-top: auto;
  background: linear-gradient(135deg, #111827 0%, #1f2937 45%, #111827 100%);
  color: #ffffff;
}

.site-footer__container {
  max-width: 80rem;
  margin-left: auto;
  margin-right: auto;
  padding: 3rem 1rem;
  box-sizing: border-box;
}

@media (min-width: 640px) {
  .site-footer__container {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
}

@media (min-width: 1024px) {
  .site-footer__container {
    padding: 4rem 2rem;
  }
}

.site-footer__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
}

@media (min-width: 768px) {
  .site-footer__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2rem 2.5rem;
  }
}

@media (min-width: 1024px) {
  .site-footer__grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 2rem 3rem;
  }
}

.site-footer__column--brand {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.site-footer__brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
  color: inherit;
  border-radius: var(--radius-sm);
}

.site-footer__brand:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

.site-footer__brand-logo-wrap {
  width: 4rem;
  height: 4rem;
  flex-shrink: 0;
  border-radius: 9999px;
  background: #ffffff;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.site-footer__brand-logo-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* Merged classes sit on the <picture> root, same as .api-responsive-image */
:deep(picture.api-responsive-image.site-footer__brand-logo-img--optimised) {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  display: block;
}

:deep(
  picture.api-responsive-image.site-footer__brand-logo-img--optimised .api-responsive-image__img
) {
  object-fit: contain;
  object-position: center;
}

.site-footer__brand-name {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.35;
  font-family: var(--font-sans);
}

.site-footer__tagline {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #d1d5db;
}

.site-footer__social-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

/* Social row in last grid column when contact column is absent: right-aligned,
   "Follow us" label inline with icons, not a column-with-heading layout. */
.site-footer__social-row {
  grid-column: -2 / -1;
  justify-self: end;
  align-self: start;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.site-footer__social-row-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #d1d5db;
}

.site-footer__social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  border-radius: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;
}

.site-footer__social-link:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

.site-footer__social-link--facebook:hover {
  background: #1877f2;
}

.site-footer__social-link--youtube:hover {
  background: #ff0000;
}

.site-footer__social-link--instagram:hover {
  background: linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888);
}

.site-footer__social-link--linkedin:hover {
  background: #0a66c2;
}

.site-footer__social-link--twitter:hover {
  background: #000000;
}

.site-footer__social-icon {
  width: 1.25rem;
  height: 1.25rem;
}

.site-footer__heading {
  margin: 0 0 1rem;
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.3;
  font-family: var(--font-sans);
}

.site-footer__link-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.site-footer__link {
  font-size: 0.875rem;
  line-height: 1.45;
  color: #d1d5db;
  text-decoration: none;
  transition: color 0.2s ease;
}

.site-footer__link:hover {
  color: var(--color-primary);
}

.site-footer__link:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
  border-radius: 2px;
}

.site-footer__contact-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.site-footer__contact-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.site-footer__contact-icon {
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
  color: var(--color-primary);
}

.site-footer__contact-text {
  font-size: 0.875rem;
  line-height: 1.45;
  color: #d1d5db;
}

.site-footer__contact-link {
  text-decoration: none;
  transition: color 0.2s ease;
}

.site-footer__contact-link:hover {
  color: var(--color-primary);
}

.site-footer__contact-link:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
  border-radius: 2px;
}

.site-footer__bottom {
  position: relative;
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #374151;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.site-footer__bottom--has-meta {
  cursor: help;
}

.site-footer__build-tooltip {
  position: absolute;
  left: 50%;
  bottom: 100%;
  transform: translateX(-50%);
  margin-bottom: 0.5rem;
  min-width: 12rem;
  max-width: min(22rem, 90vw);
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  background: #1f2937;
  border: 1px solid #4b5563;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.35);
  font-size: 0.75rem;
  line-height: 1.4;
  color: #e5e7eb;
  text-align: left;
  z-index: 20;
  pointer-events: auto;
}

.site-footer__build-tooltip-line {
  margin: 0;
}

.site-footer__build-tooltip-line + .site-footer__build-tooltip-line {
  margin-top: 0.25rem;
}

@media (min-width: 768px) {
  .site-footer__bottom {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}

.site-footer__copyright {
  margin: 0;
  font-size: 0.875rem;
  color: #9ca3af;
  text-align: center;
}

@media (min-width: 768px) {
  .site-footer__copyright {
    text-align: left;
  }
}

.site-footer__legal {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem 1.5rem;
}

@media (min-width: 768px) {
  .site-footer__legal {
    justify-content: flex-end;
  }
}

.site-footer__legal-link {
  font-size: 0.875rem;
  color: #9ca3af;
  text-decoration: none;
  transition: color 0.2s ease;
}

.site-footer__legal-link:hover {
  color: var(--color-primary);
}

.site-footer__legal-link:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
  border-radius: 2px;
}
</style>
